// Extra prism import
import 'prismjs/components/prism-diff';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-yaml';

import classNames from 'classnames';
import copyText from 'copy-to-clipboard';
import Prism from 'prismjs';
import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {Icon} from 'semantic-ui-react';

import * as S from './Code.styles';

export const CodeBlock: React.FC = ({children}) => {
  return <S.CodeBlock>{children}</S.CodeBlock>;
};

export const Bash: React.FC = React.memo(({children}) => {
  return <div className="code__block bash__terminal">{children}</div>;
});

interface CommandProps {
  children?: string | string[];
}

export const Command: React.FC<CommandProps> = React.memo(({children}) => {
  return (
    <CopyableCode>
      <div className="bash__command code__item language-bash">
        <div className="bash__command-text language-bash">{children}</div>
      </div>
    </CopyableCode>
  );
});

interface CopyableCodeProps {
  iconOnly?: boolean;
  onCopy?: () => void;
}

export const CopyableCode: React.FC<CopyableCodeProps> = React.memo(
  ({children, iconOnly, onCopy}) => {
    const [copied, setCopied] = useState(false);
    const codeRef = useRef<HTMLDivElement | null>(null);

    const copy = useCallback(() => {
      const commandText = codeRef.current ? codeRef.current.innerText : '';

      setCopied(true);
      setTimeout(() => setCopied(false), 270);
      onCopy?.();
      return copyText(commandText);
    }, [onCopy]);

    const iconClass = copied ? 'copied' : '';

    const divClickHandler = iconOnly ? undefined : copy;
    const iconClickHandler = iconOnly ? copy : undefined;

    return (
      <div className="copyable__item" onClick={divClickHandler}>
        <div ref={codeRef} className="copyable__text language-bash">
          {children}
        </div>
        <Icon
          name="copy"
          className={'copyable__copy-icon ' + iconClass}
          onClick={iconClickHandler}
        />
      </div>
    );
  }
);

export const Result: React.FC = React.memo(({children}) => {
  return <div className="code__item bash__result">{children}</div>;
});

interface PythonProps {
  style?: CSSProperties;
}

export const Python: React.FC<PythonProps> = React.memo(({style, children}) => {
  return (
    <div className="code__block" style={style}>
      <div className="python__code">{children}</div>
    </div>
  );
});

export const Yaml: React.FC = React.memo(({children}) => {
  return (
    <div className="code__block">
      <div className="yaml__code">{children}</div>
    </div>
  );
});

interface HighlightProps {
  iconOnly?: boolean;
  onCopy?: () => void;
}

export const Highlight: React.FC<HighlightProps> = React.memo(
  ({children, iconOnly, onCopy}) => {
    const codeRef = useRef<HTMLDivElement | null>(null);

    useLayoutEffect(() => {
      if (codeRef.current != null) {
        Prism.highlightElement(codeRef.current);
      }
    }, [children]);

    return (
      <CopyableCode iconOnly={iconOnly} onCopy={onCopy}>
        <div className="code__item">
          <div ref={codeRef} className="language-python">
            {children}
          </div>
        </div>
      </CopyableCode>
    );
  }
);

export const Static: React.FC<{className?: string}> = React.memo(
  ({className, children}) => {
    const codeRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      if (codeRef.current != null) {
        Prism.highlightElement(codeRef.current);
      }
    }, []);

    return (
      <div className={classNames('code__item', className)}>
        <div ref={codeRef} className="language-python">
          {children}
        </div>
      </div>
    );
  }
);

type Size = 'small' | 'medium';

interface TearProps {
  size?: Size;
  text?: string;
}

export const Tear: React.FC<TearProps> = React.memo(opts => (
  <div className={`code__tear code__tear--${opts.size}`}>{opts.text}</div>
));
